import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';

interface OnboardingContextType {
  isActive: boolean;
  currentStep: number;
  totalSteps: number;
  startOnboarding: () => void;
  nextStep: () => void;
  previousStep: () => void;
  skipOnboarding: () => void;
  completeStep: (stepId: string) => void;
  hasCompletedInSession: () => boolean;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

export function OnboardingProvider({ children }: { children: React.ReactNode }) {
  const { markOnboardingCompleted } = useAuth();
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const totalSteps = 4; // Number of steps in your onboarding modal
  const hasCompletedInSession = useRef(false); // Flag to prevent auto-trigger after completion

  const startOnboarding = useCallback(() => {
    setIsActive(true);
    setCurrentStep(0);
  }, []);

  const nextStep = useCallback(async () => {
    if (currentStep >= totalSteps - 1) {
      // Mark onboarding as completed when finishing
      try {
        await markOnboardingCompleted();
        hasCompletedInSession.current = true; // Set session flag
      } catch (error) {
        console.error('Error marking onboarding as completed:', error);
      }
      setIsActive(false);
      setCurrentStep(0);
    } else {
      setCurrentStep(prev => prev + 1);
    }
  }, [currentStep, totalSteps, markOnboardingCompleted]);

  const previousStep = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  }, [currentStep]);

  const skipOnboarding = useCallback(async () => {
    // Mark onboarding as completed when skipping
    try {
      await markOnboardingCompleted();
      hasCompletedInSession.current = true; // Set session flag
    } catch (error) {
      console.error('Error marking onboarding as completed:', error);
    }
    setIsActive(false);
    setCurrentStep(0);
  }, [markOnboardingCompleted]);

  const completeStep = useCallback((_stepId: string) => {
    // Simple implementation - just acknowledge the step completion
    // You can add any simple logic here if needed
  }, []);

  const hasCompletedInSessionCheck = useCallback(() => {
    return hasCompletedInSession.current;
  }, []);

  const value: OnboardingContextType = {
    isActive,
    currentStep,
    totalSteps,
    startOnboarding,
    nextStep,
    previousStep,
    skipOnboarding,
    completeStep,
    hasCompletedInSession: hasCompletedInSessionCheck,
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
}

export function useOnboarding() {
  const context = useContext(OnboardingContext);
  if (context === undefined) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
}